import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import { canonicalUrl } from "../../Common/canonicalUrl";
import HeroSection from "../HeroSection/HeroSection";
import HomeDetailSection from "../HomeDetailSection/HomeDetailSection";
import BrandPartners from "../company/BrandPartners/BrandPartners";
import EnquiryCta from "../HomeDetailSection/EnquiryCta";
import ShareRow from "../../Common/ShareRow";
import { COMPANY_DATA } from "../../constant/CompanyData";

// Social-card image only. The hero itself no longer paints a photo backdrop,
// so this file is never requested during a normal page view.
const HERO_IMAGE = "/assets/dataserver.webp";

const Home = () => {
  const location = useLocation();

  // 🔥 Auto-scroll when navigation state requests it (e.g. coming from Category)
  useEffect(() => {
    // Only auto-scroll when navigation set a state asking to scroll to categories.
    // This prevents unwanted scrolling on first page load when there is no navigation state.
    if (location.state && location.state.scrollTo === "categories") {
      const el = document.getElementById("categories-section");
      if (el) {
        const yOffset = -70; // adjust for sticky header height
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

        // Jump instantly — no smooth scrolling
        window.scrollTo(0, y);
      }
    }
  }, [location]);

  // Must stay identical to the static tags in public/index.html. Helmet
  // replaces those at runtime, so a crawler that renders JS reads these while
  // one that doesn't reads the file — two different values for the same page
  // otherwise. "Services" is deliberately absent: this site is the shop, and
  // services live on quantarock.in.
  const siteTitle = "Interseller — Buy Enterprise IT Hardware | Request a Quote";
  const siteDescription =
    "Buy enterprise IT hardware online at Interseller: servers, storage, networking, firewalls, components, laptops and software licences. Quotes in one working day.";

  return (
    <>
      <Helmet>
        <title>{siteTitle}</title>
        <meta name="description" content={siteDescription} />
        <link rel="canonical" href={canonicalUrl()} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDescription} />
        {/* Crawlers reject relative og:image values — always send an absolute URL */}
        <meta
          property="og:image"
          content={`${window.location.origin}${HERO_IMAGE}`}
        />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Interseller",
            url: window.location.origin,
            logo: `${window.location.origin}/logo.png`,
            telephone: COMPANY_DATA.phoneNumber,
            email: COMPANY_DATA.mailID,
            address: {
              "@type": "PostalAddress",
              streetAddress: COMPANY_DATA.address,
              addressLocality: "Indore",
              addressRegion: "Madhya Pradesh",
              postalCode: "452010",
              addressCountry: "IN",
            },
            // Every profile Google should tie back to this Organization —
            // keep in step with the footer's social row.
            sameAs: [
              COMPANY_DATA.linkedInURL,
              COMPANY_DATA.twitterURL,
              COMPANY_DATA.instaURL,
              COMPANY_DATA.facebookURL,
              COMPANY_DATA.youtubeURL,
            ],
          })}
        </script>
      </Helmet>
      <HeroSection />

      {/* ⭐ This is where the scroll will land */}
      <section id="categories-section">
        <HomeDetailSection />
      </section>

      <BrandPartners />
      <EnquiryCta />

      {/* Sharing lived only on product pages, which is where a link most often
          starts — but the store front page is what gets passed to a colleague
          who has not seen the catalogue at all. Same hand-rolled links as the
          product pages: no plugin, no third-party script. */}
      <section className="bg-white px-6 py-8">
        <div className="mx-auto flex max-w-7xl justify-center">
          <ShareRow title={siteTitle} label="Share Interseller" />
        </div>
      </section>
    </>
  );
};

export default Home;
